import { authService } from './auth.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface Advance {
  id: string;
  project_id: string;
  project_name?: string;
  contractor_id: string;
  contractor_name?: string;
  week_number: number;
  year: number;
  week_start: number;
  week_end: number;
  description?: string;
  progress_percentage: number;
  status: 'pending' | 'approved' | 'rejected' | 'draft';
  approved_by?: string;
  approved_at?: number;
  rejected_reason?: string;
  photos: AdvancePhoto[];
  created_at: number;
  updated_at: number;
}

export interface AdvancePhoto {
  id: string;
  advance_id: string;
  url: string;
  thumbnail_url?: string;
  caption?: string;
  order: number;
  uploaded_at: number;
}

export interface CreateAdvanceData {
  project_id: string;
  week_number: number;
  year: number;
  week_start: number;
  week_end: number;
  description?: string;
  progress_percentage: number;
  photos?: File[];
}

export interface UpdateAdvanceData {
  description?: string;
  progress_percentage?: number;
  status?: Advance['status'];
  rejected_reason?: string;
}

export const advanceService = {
  // ============================================================
  // OBTENER AVANCES
  // ============================================================
  
  async getAdvances(projectId?: string): Promise<Advance[]> {
    const url = projectId 
      ? `${API_URL}/api/advances?project_id=${projectId}`
      : `${API_URL}/api/advances`;
    const response = await authService.fetchWithAuth(url);
    return response.data || [];
  },

  async getAdvance(id: string): Promise<Advance> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/advances/${id}`);
    return response.data;
  },

  // ============================================================
  // CREAR/ACTUALIZAR/ELIMINAR AVANCE
  // ============================================================
  
  async createAdvance(data: CreateAdvanceData): Promise<Advance> {
    // Si hay fotos, subirlas primero
    let photoUrls: string[] = [];
    if (data.photos && data.photos.length > 0) {
      photoUrls = await this.uploadPhotos(data.photos);
    }

    const response = await authService.fetchWithAuth(`${API_URL}/api/advances`, {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        photos: photoUrls
      })
    });
    return response.data;
  },

  async updateAdvance(id: string, data: UpdateAdvanceData): Promise<Advance> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/advances/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return response.data;
  },

  async deleteAdvance(id: string): Promise<void> {
    await authService.fetchWithAuth(`${API_URL}/api/advances/${id}`, {
      method: 'DELETE'
    });
  },

  // ============================================================
  // APROBAR/RECHAZAR AVANCE
  // ============================================================
  
  async approveAdvance(id: string): Promise<Advance> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/advances/${id}/approve`, {
      method: 'POST'
    });
    return response.data;
  },

  async rejectAdvance(id: string, reason: string): Promise<Advance> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/advances/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
    return response.data;
  },

  // ============================================================
  // FOTOS
  // ============================================================
  
  async uploadPhotos(files: File[]): Promise<string[]> {
    const urls: string[] = [];
    for (const file of files) {
      // Comprimir imagen antes de subir
      const compressed = await this.compressImage(file);
      const formData = new FormData();
      formData.append('photo', compressed);
      
      const response = await authService.fetchWithAuth(`${API_URL}/api/advances/photos`, {
        method: 'POST',
        body: formData,
        headers: {
          // No establecer Content-Type, FormData lo hace automáticamente
        }
      });
      urls.push(response.data.url);
    }
    return urls;
  },

  async compressImage(file: File, maxWidth = 1200, maxHeight = 1200, quality = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
          
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('No se pudo crear el contexto de canvas'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('No se pudo comprimir la imagen'));
                return;
              }
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^.]+$/, '.webp'),
                { type: 'image/webp' }
              );
              resolve(compressedFile);
            },
            'image/webp',
            quality
          );
        };
        img.onerror = () => reject(new Error('Error al cargar la imagen'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsDataURL(file);
    });
  },

  // ============================================================
  // ESTADÍSTICAS
  // ============================================================
  
  async getAdvanceStats(projectId?: string): Promise<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    avgProgress: number;
  }> {
    const url = projectId 
      ? `${API_URL}/api/advances/stats?project_id=${projectId}`
      : `${API_URL}/api/advances/stats`;
    const response = await authService.fetchWithAuth(url);
    return response.data;
  }
};